import * as React from "react";
import { Tabs, TabsContent, FilterBlock, Button, useTableExport } from "@racoon/ui";
import { TruckTab } from "../components/autopark.truck-tab.admin";
import { ShiftsTab } from "../components/ShiftsTab";
import { useT, Namespace, useLang } from "@racoon/i18n";
import { useAutoparkUrlState } from "../hooks/useAutoparkUrlState";
import { CarsAdditionalButtons } from "../components/autopark.cars-additional-buttons.admin";
import { useShiftsUrlState } from "../hooks/useShiftsUrlState";
import { useShiftOptions } from "../hooks/useShiftOptions";
import { useActiveDriverOptions } from "~/shared/api/user/use-active-driver-options";
import { useListTrucksQuery } from "~/shared/api/truck/truck.grpc";
import { useListDriverOnShiftQuery } from "~/shared/api/shift/driver-on-shift.grpc";
import { useAuthSession } from "~/shared/hooks/auth.session.admin";
import { useSelectedCompany } from "~/shared/store/company/hooks/useSelectedCompany";
import { ConnectProto } from "@racoon/api";
import { CreateShiftModal } from "../components/CreateShiftModal";
import { useShiftGroups } from "../model/use-shift-groups";
import { ShiftsViewToggle } from "./shifts-view-toggle";
import { IcPrint } from "@racoon/icons";
import { getShiftsExportColumns } from "../components/ShiftsTable";
import type { ShiftData, ShiftUsage } from "../model/shift-data";
import { truckModelLabel } from "../helpers/shiftGroups";
import { truckTypeOf } from "~/shared/lib/truck-type";
import { useGetShiftUsageQuery } from "~/shared/api/analytics/analytics.grpc";
import { isShiftTerminal, resolveShiftReleasedAt } from "../helpers";
import { isTtnApproved } from "~/shared/lib/shift-status";
import { isTruckArchived } from "~/shared/lib/truck-type";
import { printWaybills } from "../helpers/print-waybill.helper";
import {
  useGetOrganizationSettingsQuery,
  useGetOrganizationByIdQuery,
} from "~/shared/api/organization/organization.grpc";
import { skipToken } from "@reduxjs/toolkit/query";
import { useActiveChildOrgs } from "~/shared/api/organization/use-active-child-orgs";
import { fromTimestamp, toDateParam } from "@racoon/lib";

export default function AutoparkPage() {
  const t = useT();
  const { lang } = useLang();
  const { tab, selectedState, onStateChange } = useAutoparkUrlState();

  const isShiftsTab = selectedState.value === "shifts";
  const { selectedCompany: organization } = useSelectedCompany();
  const { activeCompanyId } = useAuthSession();

  // Fetch current organization settings
  const { data: organizationSettings, refetch: refetchSettings } = useGetOrganizationSettingsQuery(
    {},
    {
      skip: !activeCompanyId || !isShiftsTab,
    },
  );

  // Fetch current organization data
  const { data: organizationData } = useGetOrganizationByIdQuery(
    { organizationId: BigInt(activeCompanyId!) },
    { skip: !activeCompanyId || !isShiftsTab },
  );

  const base = useShiftsUrlState({
    ownerOrganizationId: activeCompanyId!,
  });

  // Fetched on every tab: the «Реестр смен» badge needs the count while the
  // user is still on «Машины».
  const {
    data: shiftsData,
    isLoading: isShiftsLoading,
    refetch: refetchShifts,
  } = useListDriverOnShiftQuery(activeCompanyId ? base.apiQuery : skipToken);

  const shiftsList = React.useMemo(() => shiftsData?.driverOnShifts || [], [shiftsData]);

  // Fetch all active drivers
  const allDriversFormatted = useActiveDriverOptions(activeCompanyId, { skip: !isShiftsTab });

  const { data: trucksData } = useListTrucksQuery(
    {
      ownerOrganizationIds: [BigInt(activeCompanyId || 0)],
      statuses: [ConnectProto.TruckStatus.ACTIVE],
    },
    { skip: !activeCompanyId || !isShiftsTab },
  );

  const active_trucks = React.useMemo(() => {
    return (
      trucksData?.trucks?.filter((truck) => truck.status === ConnectProto.TruckStatus.ACTIVE) || []
    );
  }, [trucksData]);

  const { drivers, trucks: shiftTrucks } = useShiftOptions(shiftsList);

  const {
    startDate,
    endDate,
    onFilterParamsChange,
    onDateChange,
    filterParams,
    items,
    searchValue,
    onSearchChange,
  } = useShiftsUrlState({
    options: { drivers, trucks: shiftTrucks },
    data: shiftsList,
    ownerOrganizationId: activeCompanyId!,
  });

  const handleFilterParamsChange = React.useCallback(
    (key: string, value: string | null) => {
      onFilterParamsChange(key, value || "all");
    },
    [onFilterParamsChange],
  );

  // Per-shift telemetry over the same range as the list; shifts whose routes
  // never started are simply absent, which the table renders as dashes.
  const { data: shiftUsageData } = useGetShiftUsageQuery(
    activeCompanyId && isShiftsTab
      ? {
          ownerOrganizationId: BigInt(activeCompanyId),
          period: { from: toDateParam(startDate), to: toDateParam(endDate) },
        }
      : skipToken,
  );

  const usageByShiftId = React.useMemo(() => {
    const map = new Map<string, ShiftUsage>();
    for (const row of shiftUsageData?.rows || []) {
      if (row.driverOnShiftId == null) continue;
      map.set(String(row.driverOnShiftId), {
        mileageKm: row.mileageKm,
        fuelConsumedLitres: row.fuelConsumedLitres,
        fuelRefueledLitres: row.fuelRefueledLitres,
      });
    }
    return map;
  }, [shiftUsageData?.rows]);

  // Shifts come back with their driver but not the driver's organization, so a
  // driver's division is named from the active company and its child orgs (the
  // shift list spans both).
  const { children: childOrgs } = useActiveChildOrgs();
  const divisionNames = React.useMemo(() => {
    const names = new Map(childOrgs.map((org) => [String(org.id), org.name]));
    if (organizationData) names.set(String(organizationData.id), organizationData.name);
    return names;
  }, [childOrgs, organizationData]);

  const shiftsTableData = React.useMemo(() => {
    return items.map<ShiftData>((shift) => {
      // The fleet record carries the type entity and manufacturer; the shift's own truck does not.
      const fleetTruck = trucksData?.trucks?.find((truck) => truck.id === shift.truck?.id);
      return {
        id: String(shift.id ?? 0),
        status: shift.status as unknown as ShiftData["status"],
        ttnApproved: isTtnApproved(shift),
        plateNumber: shift.truck?.plateNumber || "",
        driverName: shift.driver
          ? `${shift.driver.firstName || ""} ${shift.driver.lastName || ""}`.trim()
          : "",
        driverDetails: {
          lastName: shift.driver?.lastName,
          licenseNumber: shift.driver?.driverLicenseNumber,
          division:
            shift.driver?.ownerOrganization?.name ??
            divisionNames.get(String(shift.driver?.ownerOrganizationId)),
        },
        type: String(
          fleetTruck
            ? truckTypeOf(fleetTruck)
            : shift.truck?.truckType?.type || shift.truck?.type || "",
        ),
        model: (fleetTruck && truckModelLabel(fleetTruck)) || shift.truck?.model || "",
        trailer: shift.truck?.techPassport || fleetTruck?.techPassport || "",
        releasedAt: resolveShiftReleasedAt(shift),
        mileageBefore: Number(shift.odometerReading || 0),
        usage: usageByShiftId.get(String(shift.id)),
        startedAt: shift.startedAt ? fromTimestamp(shift.startedAt)?.toISOString() : undefined,
        twStartAt: shift.twStartAt ? fromTimestamp(shift.twStartAt)?.toISOString() : undefined,
        twEndAt: shift.twEndAt ? fromTimestamp(shift.twEndAt)?.toISOString() : undefined,
        acceptedAt: shift.completedAt ? fromTimestamp(shift.completedAt)?.toISOString() : undefined,
        completed_at: shift.completedAt
          ? fromTimestamp(shift.completedAt)?.toISOString()
          : undefined,
        driverId: shift.driver?.id,
        truckId: shift.truck?.id,
        createdAt: shift.createdAt ? fromTimestamp(shift.createdAt)?.toISOString() : undefined,
      };
    });
  }, [items, trucksData, usageByShiftId, divisionNames]);

  // Running and planned shifts both get a waybill — the driver takes it before
  // the start. A shift with no driver yet is skipped by the printer, so it is
  // left out of the count too.
  const isPrintableActiveShift = React.useCallback(
    (shift: ShiftData) => !isShiftTerminal(shift.status) && !!shift.driverName,
    [],
  );

  // Grouped once for the screen and the export, so the file comes out in the
  // view's group order. Within a group rows keep the list's order — a column sort
  // applied on screen is not carried into the file.
  const { view: shiftsView, groups: shiftGroups } = useShiftGroups({
    shifts: shiftsTableData,
    trucks: active_trucks,
  });
  const exportRows = React.useMemo(
    () => shiftGroups.flatMap((group) => group.shifts),
    [shiftGroups],
  );

  // Hook for exporting the shifts table
  const { handleExportExcel } = useTableExport({
    data: exportRows,
    columns: getShiftsExportColumns(t, lang),
    reportName: "Смены",
    startDate,
    endDate,
  });

  const activeShiftsCount = React.useMemo(() => {
    return shiftsTableData.filter(isPrintableActiveShift).length;
  }, [shiftsTableData, isPrintableActiveShift]);

  const activeShifts = React.useMemo(() => {
    return (
      shiftsData?.driverOnShifts
        ?.filter((shift: ConnectProto.DriverOnShift) => !isShiftTerminal(shift.status))
        .map((shift) => ({
          driver_id: Number(shift.driver?.id || 0),
          truck_id: Number(shift.truck?.id || 0),
        })) || []
    );
  }, [shiftsData]);

  const handlePrintWaybill = React.useCallback(async () => {
    const activeShiftsToPrint = shiftsTableData.filter(isPrintableActiveShift);
    if (!activeShiftsToPrint.length) return;

    // Refresh organization settings before printing
    const { data: freshSettings } = await refetchSettings();

    const printData = activeShiftsToPrint.map((shift) => ({
      id: shift.id,
      status: shift.status,
      plateNumber: shift.plateNumber,
      driverName: shift.driverName,
      type: shift.type,
      model: shift.model,
      trailer: shift.trailer,
      releasedAt: shift.releasedAt,
      twStartAt: shift.twStartAt,
      mileageBefore: shift.mileageBefore,
      acceptedAt: shift.acceptedAt,
      completed_at: shift.completed_at,
    }));

    await printWaybills(printData, {
      organization: organizationData || organization || undefined,
      organizationSettings: freshSettings || organizationSettings || undefined,
      t,
    });
  }, [
    shiftsTableData,
    isPrintableActiveShift,
    refetchSettings,
    organizationData,
    organization,
    organizationSettings,
    t,
  ]);

  const shiftsAdditionalButtons = React.useMemo(
    () => (
      <div className="flex items-center gap-2">
        <CreateShiftModal
          drivers={allDriversFormatted}
          trucks={active_trucks}
          activeShifts={activeShifts}
          onShiftCreated={refetchShifts}
          defaultDate={startDate || undefined}
        />
        <Button
          id="shifts-tab-print-waybill-button"
          size="sm"
          variant="opacityBlueWithoutOutline"
          onClick={handlePrintWaybill}
          className="gap-2 flex-auto md:flex-initial"
          disabled={activeShiftsCount === 0}
        >
          <IcPrint className="h-4 w-4" />
          {t(Namespace.Autopark, "shifts.printWaybill")}
          {activeShiftsCount > 0 && ` (${activeShiftsCount})`}
        </Button>
        <span className="hidden md:block">
          <Button
            id="shifts-tab-export-button"
            variant="excel"
            size="sm"
            onClick={handleExportExcel}
          >
            {t(Namespace.Autopark, "shifts.downloadTable")}
          </Button>
        </span>
      </div>
    ),
    [
      allDriversFormatted,
      active_trucks,
      activeShifts,
      refetchShifts,
      startDate,
      handlePrintWaybill,
      activeShiftsCount,
      t,
      handleExportExcel,
    ],
  );

  // Same args as TruckTab's own list query, so this shares its cache entry
  // instead of firing a second request — it only feeds the tab badge.
  const { data: fleetData } = useListTrucksQuery(
    {
      ownerOrganizationIds: activeCompanyId ? [BigInt(activeCompanyId)] : [],
    },
    { skip: !activeCompanyId },
  );

  // Localized states for FilterBlock; counts are client-side — no count RPCs exist.
  const localizedStates = React.useMemo(
    () => [
      {
        key: t(Namespace.Autopark, "tabs.machines"),
        value: "cars",
        // Archived trucks sit behind their own pill inside the tab, so the badge
        // counts what «Машины» opens on.
        count: fleetData?.trucks?.filter((truck) => !isTruckArchived(truck)).length,
      },
      {
        key: t(Namespace.Autopark, "tabs.registry"),
        value: "shifts",
        count: shiftsData ? shiftsTableData.length : undefined,
      },
      // The repairs tab is still a stub — there is no data to count.
      { key: t(Namespace.Autopark, "tabs.repairList"), value: "repairs" },
    ],
    [t, fleetData?.trucks, shiftsData, shiftsTableData.length],
  );

  return (
    // The page fills the layout's scroll area exactly, so the shifts table below
    // scrolls inside itself — which is what lets its column header stay sticky.
    <div className="w-full min-w-0 flex flex-col min-h-0 h-[calc(100dvh-2rem)]">
      <FilterBlock
        states={localizedStates}
        selectedState={localizedStates.find((s) => s.value === selectedState.value)}
        onStateChange={onStateChange}
        direction={selectedState.value === "cars" ? "row" : "column"}
        additionalButtonsPosition={selectedState.value === "cars" ? "bottom" : "top"}
        additionalButtons={
          selectedState.value === "cars" ? (
            <CarsAdditionalButtons />
          ) : selectedState.value === "shifts" ? (
            shiftsAdditionalButtons
          ) : null
        }
        {...(selectedState.value === "shifts"
          ? {
              leadingControls: <ShiftsViewToggle />,
              filterParams,
              startDate,
              endDate,
              onFilterParamsChange: handleFilterParamsChange,
              onDateChange,
              searchValue,
              onSearchChange,
            }
          : {})}
      />

      <Tabs
        value={tab}
        onValueChange={(value) => onStateChange({ key: "", value })}
        className="flex flex-col flex-1 min-h-0"
      >
        <TabsContent value="cars">
          <TruckTab />
        </TabsContent>

        <TabsContent value="shifts" className="flex flex-col flex-1 min-h-0">
          <ShiftsTab
            shifts={shiftsTableData}
            view={shiftsView}
            groups={shiftGroups}
            trucks={active_trucks}
            drivers={allDriversFormatted}
            defaultDate={startDate || undefined}
            isLoading={isShiftsLoading}
            refetch={refetchShifts}
          />
        </TabsContent>

        <TabsContent value="repairs">
          <div className="text-center py-8 text-muted-foreground">
            {t(Namespace.Autopark, "repairs.inDevelopment")}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
